import type { Feature } from 'ol';
import type { ReadOptions } from 'ol/format/Feature';
import type { Options as SourceOptions } from 'ol/source/Vector';
import type { Projection as OLProjection } from 'ol/proj';

import { AbstractGeoViewVector } from '@/geo/layer/geoview-layers/vector/abstract-geoview-vector';
import type { DisplayDateMode, TypeOutfields, TypeOutfieldsType } from '@/api/types/map-schema-types';
import type {
  TypeGeoviewLayerConfig,
  TypeMetadataOGCFeature,
  TypeLayerMetadataQueryables,
  TypeLayerMetadataOGC,
} from '@/api/types/layer-schema-types';
import { CONST_LAYER_TYPES } from '@/api/types/layer-schema-types';
import { OgcFeatureLayerEntryConfig } from '@/api/config/validation-classes/vector-validation-classes/ogc-layer-entry-config';
import type { VectorLayerEntryConfig } from '@/api/config/validation-classes/vector-layer-entry-config';
import { Fetch } from '@/core/utils/fetch-helper';
import {
  LayerEntryConfigInvalidLayerEntryConfigError,
  LayerEntryConfigLayerIdNotFoundError,
} from '@/core/exceptions/layer-entry-config-exceptions';
import type { ConfigBaseClass, TypeLayerEntryShell } from '@/api/config/validation-classes/config-base-class';
import { LayerServiceMetadataUnableToFetchError } from '@/core/exceptions/layer-exceptions';
import { formatError } from '@/core/exceptions/core-exceptions';
import { Projection } from '@/geo/utils/projection';
import { GeoUtilities, type FetchWithProxyResult, type SourceFeaturesInfo } from '@/geo/utils/utilities';
import { WMS } from '@/geo/layer/geoview-layers/raster/wms';
import { GVOGCFeature } from '@/geo/layer/gv-layers/vector/gv-ogc-feature';

export interface TypeOgcFeatureLayerConfig extends Omit<TypeGeoviewLayerConfig, 'listOfLayerEntryConfig' | 'geoviewLayerType'> {
  geoviewLayerType: typeof CONST_LAYER_TYPES.OGC_FEATURE;
  fetchStylesOnWMS?: boolean;
  listOfLayerEntryConfig: OgcFeatureLayerEntryConfig[];
}

/**
 * A class to add OGC api feature layer.
 */
export class OgcFeature extends AbstractGeoViewVector {
  /**
   * Constructs a OgcFeature Layer configuration processor.
   *
   * @param layerConfig - The layer configuration
   */
  // The constructor is not useless, it narrows down the accepted parameter type.
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(layerConfig: TypeOgcFeatureLayerConfig) {
    super(layerConfig);
  }

  // #region OVERRIDES

  /**
   * Overrides the parent class's getter to provide a more specific return type (covariant return).
   *
   * @returns The strongly-typed layer configuration specific to this layer
   */
  override getGeoviewLayerConfig(): TypeOgcFeatureLayerConfig {
    return super.getGeoviewLayerConfig() as TypeOgcFeatureLayerConfig;
  }

  /**
   * Overrides the parent class's getter to provide a more specific return type (covariant return).
   *
   * @returns The strongly-typed metadata specific to this layer
   */
  override getMetadata(): TypeMetadataOGCFeature | undefined {
    return super.getMetadata() as TypeMetadataOGCFeature | undefined;
  }

  /**
   * Overrides the way the metadata is fetched.
   *
   * @param abortSignal - Optional {@link AbortSignal} used to cancel the layer creation process
   * @returns A promise that resolves with the fetched metadata and proxy information
   * @throws {LayerServiceMetadataUnableToFetchError} When the metadata fetch fails (network, proxy, or HTTP error)
   */
  protected override async onFetchServiceMetadata(abortSignal?: AbortSignal): Promise<FetchWithProxyResult<unknown>> {
    try {
      // Fetch it and return
      return await OgcFeature.fetchMetadata(this.getMetadataAccessPath(), this.getConfigProxyUrl(), abortSignal);
    } catch (error: unknown) {
      // Throw
      throw new LayerServiceMetadataUnableToFetchError(
        this.getGeoviewLayerId(),
        this.getLayerEntryNameOrGeoviewLayerName(),
        formatError(error)
      );
    }
  }

  /**
   * Overrides the way a geoview layer config initializes its layer entries.
   *
   * @returns A promise that resolves once the layer entries have been initialized
   * @throws {LayerServiceMetadataUnableToFetchError} When the metadata fetch fails or contains an error
   */
  protected override async onInitLayerEntries(): Promise<TypeGeoviewLayerConfig> {
    // Get the folder url
    const sep = '/collections/';
    const idx = this.getMetadataAccessPath().lastIndexOf(sep);
    let rootUrl = this.getMetadataAccessPath();
    let id: string | undefined;
    let entries: TypeLayerEntryShell[] = [];
    if (idx > 0) {
      rootUrl = this.getMetadataAccessPath().substring(0, idx);
      id = this.getMetadataAccessPath().substring(idx + sep.length);
      entries = [{ id }];
    }

    // If no id
    if (!id) {
      // Calls fetchServiceMetadata which delegates to this class's overridden onFetchServiceMetadata (may use a proxy fallback and store the proxyUrl on the instance)
      const fetchResult = await this.fetchServiceMetadata<TypeMetadataOGCFeature>();

      // Now that we have metadata
      entries = fetchResult.data.collections.map((collection) => {
        return { id: collection.id, layerId: collection.id, layerName: collection.description };
      });
    }

    // Redirect
    return OgcFeature.createGeoviewLayerConfig(
      this.getGeoviewLayerId(),
      this.getGeoviewLayerName(),
      rootUrl,
      this.getGeoviewLayerConfig().isTimeAware,
      entries
    );
  }

  /**
   * Overrides the validation of a layer entry config.
   *
   * @param layerConfig - The layer entry config to validate
   */
  protected override onValidateLayerEntryConfig(layerConfig: ConfigBaseClass): void {
    // Note that the code assumes ogc-feature collections does not contains metadata layer group. If you need layer group,
    // you can define them in the configuration section.

    // Get the metadata
    const metadata = this.getMetadata();

    if (Array.isArray(metadata?.collections)) {
      const foundCollection = metadata.collections.find((layerMetadata) => layerMetadata.id === layerConfig.layerId);
      if (!foundCollection) {
        // Add a layer load error
        this.addLayerLoadError(new LayerEntryConfigLayerIdNotFoundError(layerConfig), layerConfig);
        return;
      }

      // Initialize the layer name by filling the blanks with the name from the metadata
      layerConfig.initLayerNameFromMetadata(foundCollection.description);

      // If no bounds defined in the initial settings and an extent is defined in the metadata
      let bounds = layerConfig.getInitialSettingsBounds();
      if (!bounds && foundCollection.extent?.spatial?.bbox && foundCollection.extent?.spatial?.crs) {
        // Project the latlong
        bounds = Projection.transformExtentFromProj(
          foundCollection.extent.spatial.bbox[0],
          Projection.getProjectionFromStringOrNumber(foundCollection.extent.spatial.crs),
          Projection.getProjectionLonLat()
        );

        // Validate and update the bounds initial settings
        layerConfig.initInitialSettingsBoundsFromMetadata(bounds);
      }

      // Done
      return;
    }

    // Failed
    throw new LayerEntryConfigInvalidLayerEntryConfigError(layerConfig);
  }

  /**
   * Overrides the way the layer metadata is processed.
   *
   * @param layerConfig - The layer entry configuration to process
   * @param displayDateMode - The display date mode to use for processing time dimensions in the metadata
   * @param mapProjection - Optional map projection
   * @param abortSignal - Optional {@link AbortSignal} used to cancel the layer creation process
   * @returns A promise that resolves once the layer entry configuration has gotten its metadata processed
   * @throws {LayerMetadataAccessPathMandatoryError} When the collection queryables metadata path is not configured
   * @throws {RequestTimeoutError} When the collection queryables request exceeds the timeout duration
   * @throws {RequestAbortedError} When the collection queryables request is aborted
   * @throws {ResponseError} When the collection queryables response is not successful
   * @throws {ResponseEmptyError} When the collection queryables response is empty
   * @throws {NetworkError} When the collection queryables request encounters a network error
   */
  protected override async onProcessLayerMetadata(
    layerConfig: VectorLayerEntryConfig,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    displayDateMode: DisplayDateMode,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mapProjection?: OLProjection,
    abortSignal?: AbortSignal
  ): Promise<VectorLayerEntryConfig> {
    // Cast it
    const layerConfigOgcFeature = layerConfig as OgcFeatureLayerEntryConfig;

    // The metadata url
    const metadataUrl = layerConfigOgcFeature.getMetadataAccessPath(true);

    // The url suffix
    const urlSuffix = `collections/${layerConfigOgcFeature.layerId}/queryables?f=json`;

    // If there is a metadata url
    if (metadataUrl) {
      // The query url
      const queryUrl = `${layerConfigOgcFeature.getMetadataAccessPathProxiedWhenNecessary(true)}${urlSuffix}`;

      // Query the metadata for the queryables
      const queryResultData = await Fetch.fetchJson<TypeLayerMetadataQueryables>(queryUrl, { signal: abortSignal });

      // Keep the non-proxied full URL of the queryables request
      layerConfigOgcFeature.setQueryablesFullUrl(`${metadataUrl}${urlSuffix}`);

      // Init the layer metadata
      OgcFeature.initLayerMetadata(layerConfigOgcFeature, queryResultData);

      // Try
      const layerStyle = await WMS.tryProcessLayerStylingInformationIfAny(layerConfigOgcFeature);

      // Initialize the layer style by filling the blanks with the information from the metadata
      layerConfigOgcFeature.initLayerStyleFromMetadata(layerStyle);
    }

    // Return the layer config
    return layerConfigOgcFeature;
  }

  /**
   * Overrides the loading of the vector features for the layer by fetching OGC Feature data and converting it
   * into OpenLayers {@link Feature} feature instances.
   *
   * @param layerConfig - The configuration object for the vector layer, containing source and data access information
   * @param sourceOptions - The OpenLayers vector source options associated with the layer
   * @param readOptions - Options controlling how features are read, including the target `featureProjection`
   * @returns A promise that resolves to an array of OpenLayers features
   */
  protected override async onCreateVectorSourceLoadFeatures(
    layerConfig: VectorLayerEntryConfig,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sourceOptions: SourceOptions<Feature>,
    readOptions: ReadOptions
  ): Promise<SourceFeaturesInfo> {
    // Build the URL
    const url = `${layerConfig.getDataAccessPathProxiedWhenNecessary(true)}collections/${layerConfig.layerId}/items`;

    // Fetch with proxy fallback support; surface a specific message if the source can't be loaded (e.g. unreachable/404)
    const responseData = await AbstractGeoViewVector.fetchSourceForLayer(layerConfig, () => Fetch.fetchJson(url));

    // Read the features
    return GeoUtilities.readFeaturesFromGeoJSON(responseData, readOptions.dataProjection, readOptions.featureProjection);
  }

  /**
   * Overrides the creation of the GV Layer.
   *
   * @param layerConfig - The layer entry configuration
   * @returns The GV Layer
   */
  protected override onCreateGVLayer(layerConfig: OgcFeatureLayerEntryConfig): GVOGCFeature {
    // Create the source
    const source = this.createVectorSource(layerConfig);
    // Create the GV Layer
    const gvLayer = new GVOGCFeature(source, layerConfig);
    // Return it
    return gvLayer;
  }

  // #endregion OVERRIDES

  // #region STATIC PUBLIC METHODS

  /**
   * Creates a configuration object for an OGC Feature layer.
   *
   * This function constructs a `TypeOgcFeatureLayerConfig` object that describes an OGC Feature layer
   * and its associated entry configurations based on the provided parameters.
   *
   * @param geoviewLayerId - A unique identifier for the GeoView layer
   * @param geoviewLayerName - The display name of the GeoView layer
   * @param metadataAccessPath - The full service URL to the layer endpoint
   * @param isTimeAware - Indicates whether the layer supports time-based filtering
   * @param layerEntries - An array of layer entries objects to be included in the configuration
   * @returns The constructed configuration object for the OGC Feature layer
   */
  static createGeoviewLayerConfig(
    geoviewLayerId: string,
    geoviewLayerName: string | undefined,
    metadataAccessPath: string,
    isTimeAware: boolean | undefined,
    layerEntries: TypeLayerEntryShell[]
  ): TypeOgcFeatureLayerConfig {
    const geoviewLayerConfig: TypeOgcFeatureLayerConfig = {
      geoviewLayerId,
      geoviewLayerName,
      metadataAccessPath,
      geoviewLayerType: CONST_LAYER_TYPES.OGC_FEATURE,
      isTimeAware,
      listOfLayerEntryConfig: [],
    };
    geoviewLayerConfig.listOfLayerEntryConfig = layerEntries.map((layerEntry) => {
      // TODO: REFACTOR IMPORTANT - This spreading should be rewritten like explained in GeoJSON file. Search id : 59026aa9

      const layerEntryConfig = new OgcFeatureLayerEntryConfig({
        geoviewLayerConfig,
        layerId: `${layerEntry.id}`,
        ...(layerEntry.layerName && { layerName: `${layerEntry.layerName}` }),
      });
      return layerEntryConfig;
    });

    // Return it
    return geoviewLayerConfig;
  }

  /**
   * Initializes a GeoView layer configuration for an OGC Feature layer.
   *
   * This method creates a basic TypeGeoviewLayerConfig using the provided
   * ID, name, and metadata access path URL. It then initializes the layer entries by calling
   * `initGeoViewLayerEntries`, which may involve fetching metadata or sublayer info.
   *
   * @param geoviewLayerId - A unique identifier for the layer
   * @param geoviewLayerName - The display name of the layer
   * @param metadataAccessPath - The full service URL to the layer endpoint
   * @param isTimeAware - Indicates whether the layer supports time-based filtering
   * @returns A promise that resolves to an initialized GeoView layer configuration with layer entries
   */
  static initGeoviewLayerConfig(
    geoviewLayerId: string,
    geoviewLayerName: string,
    metadataAccessPath: string,
    isTimeAware?: boolean
  ): Promise<TypeGeoviewLayerConfig> {
    // Create the Layer config
    const myLayer = new OgcFeature({ geoviewLayerId, geoviewLayerName, metadataAccessPath, isTimeAware } as TypeOgcFeatureLayerConfig);
    return myLayer.onInitLayerEntries();
  }

  /**
   * Initializes the metadata for a given OGC Feature layer entry configuration.
   *
   * This method processes the service metadata queryables and sets the relevant information on the layer entry configuration,
   * such as outfields and their aliases, which are essential for configuring the feature info retrieval and display.
   *
   * @param layerConfig - The OGC Feature layer entry configuration to initialize with metadata
   * @param metadataQueryables - The queryables metadata retrieved from the OGC service, containing information about the fields and
   * their types for the layer entry
   */
  static initLayerMetadata(layerConfig: VectorLayerEntryConfig, metadataQueryables: TypeLayerMetadataQueryables): void {
    if (metadataQueryables.properties) {
      layerConfig.setLayerMetadata(metadataQueryables.properties);
      OgcFeature.#processFeatureInfoConfig(metadataQueryables.properties, layerConfig);
    }
  }

  /**
   * Processes an OGC Feature GeoviewLayerConfig and returns a promise
   * that resolves to an array of `ConfigBaseClass` layer entry configurations.
   *
   * This method:
   * 1. Creates a Geoview layer configuration using the provided parameters.
   * 2. Instantiates a layer with that configuration.
   * 3. Processes the layer configuration and returns the result.
   *
   * @param geoviewLayerId - The unique identifier for the GeoView layer
   * @param geoviewLayerName - The display name for the GeoView layer
   * @param url - The URL of the service endpoint
   * @param configProxyUrl - Proxy URL to use when necessary
   * @param layerEntries - An array of layer entry shells to include in the configuration
   * @param isTimeAware - Indicates if the layer is time aware
   * @param fetchStylesOnWMS - Indicates whether to fetch styles from WMS
   * @returns A promise that resolves to an array of layer configurations
   * @throws {LayerServiceMetadataUnableToFetchError} When OGC API Features service metadata cannot be fetched
   * @throws {LayerServiceMetadataEmptyError} When the service metadata response is empty
   * @throws {LayerEntryConfigLayerIdNotFoundError} When a configured collection is not found
   * @throws {LayerEntryConfigInvalidLayerEntryConfigError} When the OGC API Features service metadata is invalid
   * @throws {LayerEntryConfigEmptyLayerGroupError} When an empty layer group prevents the root layer from being created
   * @throws {LayerEntryConfigUnableToCreateGroupLayerError} When a layer group cannot be created
   * @throws {LayerMetadataAccessPathMandatoryError} When a collection queryables metadata path is not configured
   * @throws {RequestTimeoutError} When a collection queryables request exceeds the timeout duration
   * @throws {RequestAbortedError} When a collection queryables request is aborted
   * @throws {ResponseError} When a collection queryables response is not successful
   * @throws {ResponseEmptyError} When a collection queryables response is empty
   * @throws {NetworkError} When a collection queryables request encounters a network error
   * @throws {AggregateError} When multiple layer entries fail to process
   */
  static processGeoviewLayerConfig(
    geoviewLayerId: string,
    geoviewLayerName: string,
    url: string,
    configProxyUrl: string | undefined,
    layerEntries: TypeLayerEntryShell[],
    isTimeAware: boolean,
    fetchStylesOnWMS: boolean
  ): Promise<ConfigBaseClass[]> {
    // Create the Layer config
    const layerConfig = OgcFeature.createGeoviewLayerConfig(geoviewLayerId, geoviewLayerName, url, isTimeAware, layerEntries);

    // Keep track if fetching styles on the WMS
    layerConfig.fetchStylesOnWMS = fetchStylesOnWMS;

    // Create the class from geoview-layers package
    const myLayer = new OgcFeature(layerConfig);

    // Set the config proxy url, if any in case the layer needs a proxy during processing
    myLayer.setConfigProxyUrl(configProxyUrl);

    // Process it
    return AbstractGeoViewVector.processConfig(myLayer);
  }

  /**
   * Fetches the metadata for a typical OGCFeature class.
   *
   * @param url - The url to query the metadata from
   * @param configProxyUrl - Proxy URL to use when necessary
   * @param abortSignal - Optional {@link AbortSignal} used to cancel the layer creation process
   * @returns A promise that resolves with the fetched metadata and proxy information
   * @throws {RequestTimeoutError} When the request exceeds the timeout duration
   * @throws {RequestAbortedError} When the request was aborted by the caller's signal
   * @throws {ResponseError} When the response is not OK (non-2xx)
   * @throws {ResponseEmptyError} When the JSON response is empty
   * @throws {NetworkError} When a network issue happened
   */
  static fetchMetadata(
    url: string,
    configProxyUrl: string | undefined,
    abortSignal?: AbortSignal
  ): Promise<FetchWithProxyResult<TypeMetadataOGCFeature>> {
    // The url
    let queryUrl = url.endsWith('/') ? `${url}collections` : `${url}/collections`;
    queryUrl = `${queryUrl}?f=json`;

    // Redirect to GeoUtilities
    return GeoUtilities.fetchJsonWithProxyFallback(queryUrl, configProxyUrl, abortSignal);
  }

  // #endregion STATIC PUBLIC METHODS

  // #region STATIC PRIVATE METHODS

  /**
   * This method sets the outfields and aliasFields of the source feature info.
   *
   * @param fields - An array of field names and its aliases
   * @param layerConfig - The vector layer entry to configure
   */
  static #processFeatureInfoConfig(fields: TypeLayerMetadataOGC, layerConfig: VectorLayerEntryConfig): void {
    // Get the outfields
    let outfields = layerConfig.getOutfields();

    // Process undefined outfields or aliasFields
    if (!outfields?.length) {
      // Create it
      outfields = [];

      // Loop
      Object.keys(fields).forEach((fieldEntryKey) => {
        if (fields[fieldEntryKey].type === 'Geometry') return;

        if (!fields[fieldEntryKey]) return;

        const fieldEntry = fields[fieldEntryKey];
        if (fieldEntry.type === 'Geometry') return;

        let fieldType = 'string' as TypeOutfieldsType;
        if (fieldEntry.type === 'date') fieldType = 'date';
        else if (['bigint', 'number'].includes(typeof fieldEntry)) fieldType = 'number';

        const newOutfield: TypeOutfields = {
          name: fieldEntryKey,
          alias: fieldEntryKey,
          type: fieldType,
        };
        outfields!.push(newOutfield);
      });

      // Set it
      layerConfig.setOutfields(outfields);
    }

    // Initialize the aliases
    layerConfig.initOutfieldsAliases();
  }

  // #endregion STATIC PRIVATE METHODS
}
